import React, { useEffect, useRef, useState } from "react";
import { Ban } from "lucide-react";

export interface Swatch {
    label: string;
    value: string;
}

interface Props {
    icon: React.ReactNode;
    title: string;
    swatches: Swatch[];
    // Drives the underline strip on the toolbar button and the selected ring.
    activeColor: string | null;
    clearLabel: string;
    onPick: (value: string) => void;
    onClear: () => void;
}

export default function ColorMenu({ icon, title, swatches, activeColor, clearLabel, onPick, onClear }: Props) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDocClick = (e: MouseEvent) => {
            if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    return (
        <div className="m-color-menu-wrap" ref={wrapRef}>
            <button
                className={`m-toolbar-btn${activeColor ? " active" : ""}`}
                onClick={() => setOpen(o => !o)}
                title={title}
                type="button"
            >
                {icon}
                <span className="m-color-menu-bar" style={{ background: activeColor || "#cbd5e1" }} />
            </button>
            {open && (
                <div className="m-color-menu" role="menu">
                    <div className="m-color-menu-grid">
                        {swatches.map(s => (
                            <button
                                key={s.value}
                                type="button"
                                className={`m-color-swatch${activeColor === s.value ? " selected" : ""}`}
                                style={{ background: s.value }}
                                title={s.label}
                                aria-label={s.label}
                                onClick={() => { onPick(s.value); setOpen(false); }}
                            />
                        ))}
                    </div>
                    <button
                        type="button"
                        className="m-color-menu-clear"
                        onClick={() => { onClear(); setOpen(false); }}
                    >
                        <Ban size={13} /> {clearLabel}
                    </button>
                </div>
            )}
        </div>
    );
}
